import * as Ably from 'ably';
import { firstValueFrom } from 'rxjs';

import { useAuthStore } from '~/store/auth';

// One shared client for the whole session. Every consumer (the realtime plugin,
// presence/typing rooms, view-models) reuses this single connection, so there
// is exactly one websocket and one token fetch (renewed on expiry) rather than
// one per call site.
let client: Ably.Realtime | null = null;

// The token request is authed with the in-memory access token. An expired (or not-yet-set) token
// is handled by the gateway client, which refreshes and replays the request through the shared
// single-flight coordinator. Refreshing here as well would race that one, and with refresh-token
// rotation on, the loser would present a burned token and trip Auth0's reuse detection.
async function fetchTokenRequest(): Promise<unknown> {
  const { dispatch } = useGateway();

  const res = await firstValueFrom(dispatch.realtime.tokenRequest());

  if (!res.data) throw new Error('Unable to obtain Ably token request');

  return res.data;
}

function getClient(): Ably.Realtime {
  if (client) return client;

  // clientId is pinned by the server-issued token, so it is omitted here.
  client = new Ably.Realtime({
    authCallback: async (_tokenParams, callback) => {
      // A tab with no session (login pages, or idle after logout) must not hit the token-request
      // endpoint: the guaranteed 401 has no refresh to fall back on (ENG-5882). Fail locally with a
      // retryable 401 so Ably picks the token up once a session exists. Session evidence mirrors
      // runRefresh() in token-refresh.ts: the authenticated cookie counts, so a reloading logged-in
      // user (tokens not yet restored) rides the gateway's refresh instead of waiting out a retry.
      const auth = useAuthStore();
      if (!auth.accessToken && !auth.refreshToken && !auth.authenticated) {
        callback(new Ably.ErrorInfo('Realtime auth skipped: no session', 40100, 401), null);

        return;
      }

      try {
        callback(null, (await fetchTokenRequest()) as Ably.TokenRequest);
      } catch (error) {
        callback(error as Ably.ErrorInfo, null);
      }
    },
    logLevel: 1,
  });

  return client;
}

// Vite swaps this module on every save in dev; without closing the old client
// each edit would leave an orphaned connection re-authing in the background.
const hot = (import.meta as unknown as { hot?: { dispose(cb: () => void): void } }).hot;
if (hot) {
  hot.dispose(() => {
    client?.close();
    client = null;
  });
}

export default function useRealtime() {
  // `new Ably.Realtime` connects (and fires authCallback) immediately, and useRealtime() is reached
  // on every page via the realtime plugin and the notification store — including /login/*
  // (ENG-5882). So construction is deferred to the first real use, which only happens once
  // authenticated.
  function subscribe(channel: string, event: string, callback: (message: Ably.Message) => void): void {
    getClient().channels.get(channel).subscribe(event, callback);
  }

  function unsubscribe(channel: string, event: string): void {
    // Never open a connection just to tear down a subscription that cannot exist.
    if (!client) return;

    client.channels.get(channel).unsubscribe(event);
  }

  return {
    // The live client's connection, or null. Reading it must never be what constructs a
    // client (ENG-5882) — with no socket there is nothing to observe.
    get connection() {
      return client?.connection ?? null;
    },
    get instance() {
      return getClient();
    },
    subscribe,
    unsubscribe,
  };
}
